/**
Template de la scene THREE.JS
Nassim El Khantour 2022
Hesites pas a restructurer la Class comme tu veux.

THREE docu: https://threejs.org/docs/index.html#manual/en/introduction/Creating-a-scene
GTLFLoader docu: https://threejs.org/docs/index.html?q=gltf#examples/en/loaders/GLTFLoader
OrbitControls docu: https://threejs.org/docs/index.html?q=orbit#examples/en/controls/OrbitControls

*/
import { gsap, Power2, Back, Power1 } from "gsap";

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';


const BACKGROUND = 0x000000;             //background du Canvas
const INIT_WIDTH = window.innerWidth;  //width du canvas
const INIT_HEIGHT = window.innerHeight; //height du canvas
const velocity = (function(settings){
  settings = settings || {};

  var lastPos, newPos, timer, delta,
      delay = settings.delay || 50; // in "ms" (higher means lower fidelity )

  function clear() {
    lastPos = null;
    delta = 0;
  }

  clear();

  return () => {
    newPos = window.scrollY;
    if ( lastPos != null ){ // && newPos < maxScroll
      delta = newPos -  lastPos;
    }
    lastPos = newPos;
    clearTimeout(timer);
    timer = setTimeout(clear, delay);
    return delta;
  };
})(); /*Get scroll velocity*/

export default class Scene {

  constructor({ containerId, width, height, backgroundColor }) {

    this.renderer;
    this.scene;
    this.camera;
    this.loader = new GLTFLoader();
    this.width = width || INIT_WIDTH;
    this.height = height || INIT_HEIGHT;
    this.containerId = containerId || null;
    this.container = null;
    this.backgroundColor = backgroundColor || BACKGROUND;
    this.group = new THREE.Group();
    this.allObjectLoaded = false;

  }

  spawn() {

    //check si le canvas est present dans le DOM
    this.container = document.getElementById(this.containerId);
    if (!this.container) {
      console.warn("Canvas element not found in DOM, check CanvasId parameter");
      return null;
    }

    //initialise la scene, le render et les events listeners
    this._init_();
    this._render_();
    this._events_();

  }

  _events_() {
    /*
      place tes events listeners ici (scroll, resize...)
    */
    this.container.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.container.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    this.container.addEventListener('mousedown', this.onMouseDown.bind(this));
    this.container.addEventListener('mouseup', this.onMouseUp.bind(this));
    this.container.addEventListener('onclick', this.onClick.bind(this));
    window.addEventListener('scroll', this.onScroll.bind(this));
  }

  _init_() {

    //-------Setup le Renderer-------
    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    this.renderer.setSize(this.width, this.height);
    this.container.appendChild(this.renderer.domElement);    //append le render dans le canvas

    //-------Setup de la scene-------
    this.scene = new THREE.Scene();
    this.scene.background = null;
    this.scene.autoUpdate = true;

    //-------Setup de la camera-------
    this.camera = new THREE.PerspectiveCamera(45, this.width / this.height, 0.1, 1000);
    this.camera.focalLength = 125;
    this.camera.position.z = 6;
    this.scene.add(this.camera); //ajout de la camera


    //-------Setup des lumières-------
    const ambientLight = new THREE.AmbientLight(0xFFFFFF, 1);
    this.scene.add(ambientLight);

    //-------Setup le model-------
    this.loadModel('cube').then(models => {
      this.allObjectLoaded = true;
      return models;
    }).catch(e => {
      //console.log(e);
    });

  }

  _render_() {
    //update la frame
    requestAnimationFrame(this._render_.bind(this));
    this.renderer.render(this.scene, this.camera);
  }

  loadModel(model) {

    /*
      load les models a l'aide du GLTFLoader, puis on resolve une fois que l'objet est loaded
    */

    return new Promise((resolve, reject) => {
      this.loader.load(`/wp-content/themes/redboxmediarefonte2022/resources/scripts/vendor/cube.gltf`,
        (gltf) => {
          //on loaded
          //tweak les materials
          gltf.scene.traverse(node => {
            if (node.name === 'cache') {
              const material = new THREE.MeshBasicMaterial({ color: 0xe7e9e6, colorWrite: false });
              node.material = material;
              node.renderOrder = 0;
            } else if (node.type === 'Mesh') {
              node.renderOrder = 1;

              const basicMaterial = new THREE.MeshBasicMaterial({ color: 0xFFFFFF, map: node.material.map });
              node.material = basicMaterial;

            }
          });

          this.group.add(gltf.scene);
          this.group.rotation.set(Math.PI / 5, Math.PI / 4, 0);
          this.scene.add(this.group);  //Ajoute l'objet a la scene
          resolve(gltf.scene);
        },                                 //on progress
        (error) => { reject(); }               //on error
      );

    });

  }

  /*
  EVENTS
  */


  onMouseEnter() {

    const space = 0.3;
    //rotate cube
    gsap.to(this.group.rotation, {
      duration: 0.6,
      y: this.group.rotation.y + Math.PI,
      ease: Back.easeInOut.config(3),
    });

    //add space between faces
    this.group.traverse(node => {

      switch (node.name) {

        case 'mail':
          gsap.to(node.position, { duration: 0.3, y: space, ease: Power2.easeOut });
          break;

        case 'plane':
          gsap.to(node.position, { duration: 0.3, z: space, ease: Power2.easeOut });
          break;

        case 'bulb':
          gsap.to(node.position, { duration: 0.3, z: -space, ease: Power2.easeOut });
          break;

        case 'chat':
          gsap.to(node.position, { duration: 0.3, x: space, ease: Power2.easeOut });
          break;

        case 'phone':
          gsap.to(node.position, { duration: 0.3, x: -space, ease: Power2.easeOut });
          break;


      }

    });

  }

  onMouseLeave() {

    //rotate cube
    gsap.to(this.group.rotation, {
      duration: 0.6,
      y: this.group.rotation.y - Math.PI,
      ease: Back.easeInOut.config(3),
    });

    //reset scale
    gsap.to(this.group.scale, {
      duration: 0.6,
      x: 1,
      y: 1,
      z: 1,
      ease: Back.easeInOut.config(3),
    });



    //reset position on mouse leave
    this.group.traverse(node => {
      if (node.type === "Mesh") {
        gsap.to(node.position, { duration: 0.2, x: 0, y: 0, z: 0, ease: Power2.easeOut });
        gsap.to(node.scale, { duration: 0, x: 1, y: 1, z: 1, ease: Power2.easeOut });
      }
    });
  }

  onClick() {
    const clickAnim = gsap.timeline({ duration: 0.4 });
    clickAnim.to(this.group.scale, { x: 1.2, y: 1.2, z: 1.2, ease: Back.easeInOut.config(3) });
    clickAnim.to(this.group.scale, { x: 1, y: 1, z: 1, ease: Back.easeInOut.config(3) });
  }

  onMouseDown() {
    gsap.to(this.group.scale, {
      duration: 0.4,
      x: 1.2,
      y: 1.2,
      z: 1.2,
      ease: Back.easeInOut.config(3),
    });
  }

  onMouseUp() {
    gsap.to(this.group.scale, {
      duration: 0.6,
      x: 1,
      y: 1,
      z: 1,
      ease: Back.easeInOut.config(3),
    });
  }

  onScroll() {
    gsap.to(this.group.rotation,{
      overwrite:true,
      duration:0.5,
      y:this.group.rotation.y + (velocity() > 0 ? 1 : -1) * Math.PI/4,
      ease: Power1.easeOut
    });
  }


}
